import {
  BillingAddonReferenceFragment,
  CheckSubscriptionItemsInstalledDocument,
  CheckSubscriptionItemsInstalledQuery,
  CheckSubscriptionItemsInstalledQueryVariables,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ModuleStatus, hasEnterpriseVariant } from 'modules/marketplace_modules/lib/getModuleStatus'
import getF25Plans from 'modules/upsell/lib/selfUpsell/plans/getF25Plans'
import {
  BUSINESS_VARIANT,
  ENTERPRISE_VARIANT,
  getCountryFromPlan,
  getCurrentPeriod,
  mapToF25Addon,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import { PeriodUnit } from 'modules/upsell/lib/selfUpsell/types'

type VariantType = typeof BUSINESS_VARIANT | typeof ENTERPRISE_VARIANT | null

type SubscriptionItems =
  CheckSubscriptionItemsInstalledQuery['billing']['subscriptionItemsConnection']['nodes']

const isMultiModuleArray = (
  ref: BillingAddonReferenceFragment[] | BillingAddonReferenceFragment[][]
): ref is BillingAddonReferenceFragment[][] => {
  return Array.isArray(ref?.[0])
}

const getVariantIds = (plans: ReturnType<typeof getF25Plans>['f25Addons'], period: PeriodUnit) => {
  return {
    businessVariantId: plans.at(0)?.getSpecificVariant(BUSINESS_VARIANT)?.pricing.getPlanId(period),
    enterpriseVariantId: plans
      .at(0)
      ?.getSpecificVariant(ENTERPRISE_VARIANT)
      ?.pricing.getPlanId(period),
  }
}

const getInstalledVariantAndAddonId = (
  subscriptionItems: SubscriptionItems,
  businessVariantId: string | undefined,
  enterpriseVariantId: string | undefined
): { variant: VariantType; installedAddonInternalId: string | null } => {
  let foundVariant: VariantType = null
  let installedAddonInternalId: string | null = null

  for (const item of subscriptionItems) {
    if (foundVariant) break

    if (businessVariantId && item.itemIds?.includes(businessVariantId)) {
      foundVariant = BUSINESS_VARIANT
      installedAddonInternalId = businessVariantId
    } else if (enterpriseVariantId && item.itemIds?.includes(enterpriseVariantId)) {
      foundVariant = ENTERPRISE_VARIANT
      installedAddonInternalId = enterpriseVariantId
    }
  }

  return { variant: foundVariant, installedAddonInternalId }
}

const processAddonReference = (
  addonRef: BillingAddonReferenceFragment[],
  country: string,
  period: PeriodUnit,
  subscriptionItems: SubscriptionItems
): ModuleStatus => {
  if (!addonRef || addonRef.length === 0) {
    return {
      isInstalled: false,
      variant: null,
      installedAddonInternalId: null,
      from: 'subscription',
    }
  }

  const plans = getF25Plans({ allF25Addons: mapToF25Addon(addonRef) }, country).f25Addons

  if (plans.length === 0) {
    return {
      isInstalled: false,
      variant: null,
      installedAddonInternalId: null,
      from: 'subscription',
    }
  }

  const { businessVariantId, enterpriseVariantId } = getVariantIds(plans, period)
  const { variant, installedAddonInternalId } = getInstalledVariantAndAddonId(
    subscriptionItems,
    businessVariantId,
    enterpriseVariantId
  )

  return {
    isInstalled: variant !== null,
    variant,
    installedAddonInternalId,
    from: 'subscription',
  }
}

export const useCheckSubscriptionItemsInstalled = (
  addonReference: BillingAddonReferenceFragment[] | BillingAddonReferenceFragment[][],
  variables: CheckSubscriptionItemsInstalledQueryVariables
) => {
  const { company } = useCurrentSession()
  const { loading, data, error } = useQuery(CheckSubscriptionItemsInstalledDocument, {
    variables: { ...variables },
  })

  const period = (getCurrentPeriod(company.subscription.plan) || 'month') satisfies PeriodUnit
  const legalEntityCountry = company.legalEntities.find((entity) => entity.main)?.country || ''
  const country = getCountryFromPlan(company.subscription.plan, legalEntityCountry)
  const subscriptionItems = data?.billing?.subscriptionItemsConnection?.nodes || []

  if (isMultiModuleArray(addonReference)) {
    const moduleStatusMap = new Map<string, ModuleStatus>()

    addonReference.forEach((moduleAddonRef) => {
      const key = moduleAddonRef[0]?.internalTitle
      if (key) {
        const status = processAddonReference(moduleAddonRef, country, period, subscriptionItems)
        moduleStatusMap.set(key, status)
      }
    })

    const firstModuleStatus = addonReference[0]?.[0]?.internalTitle
      ? moduleStatusMap.get(addonReference[0][0].internalTitle)
      : undefined

    return {
      loading,
      data: subscriptionItems,
      error,
      moduleStatusMap,
      isInstalled: firstModuleStatus?.isInstalled ?? false,
      installedVariant: firstModuleStatus?.variant ?? null,
      hasEnterpriseVariant: addonReference.some((refs) => hasEnterpriseVariant(refs, country)),
    }
  }
  const status = processAddonReference(addonReference, country, period, subscriptionItems)

  const moduleStatusMap = new Map<string, ModuleStatus>()
  const key = addonReference[0]?.internalTitle
  if (key) {
    moduleStatusMap.set(key, status)
  }

  return {
    loading,
    data: subscriptionItems,
    error,
    isInstalled: status.isInstalled,
    installedVariant: status.variant,
    hasEnterpriseVariant: hasEnterpriseVariant(addonReference, country),
    moduleStatusMap,
  }
}
